import { Form, Row, Col, Button, Typography, Flex } from "antd";
import { useTranslate } from "@refinedev/core";
import { Card, Skeleton } from "@libertify/ui";
import {
  BrandKit,
  ContactResponse,
  SavedSettingsResponse,
  SavedSettingsUpdateRequest,
} from "pages/media/types";
import { ThemeResponse } from "pages/media/components/themeEditor/hooks/useThemeApi";
import { PlaybookFormValues, SelectableAspectRatio } from "../types";
import { usePlaybookForm } from "../hooks/usePlaybookForm";
import useIsAdmin from "hooks/useIsAdmin/";
import { useCallback, useState } from "react";
import { MainSection } from "./PlaybookSections/MainSection";
import { BasicSection } from "./PlaybookSections/BasicSection";
import {
  AdvancedSection,
  ADVANCED_PANEL_KEYS,
} from "./PlaybookSections/AdvancedSection";

export type PlaybookFormProps = {
  playbook: SavedSettingsResponse;
  contacts: ContactResponse[];
  brandKits: BrandKit[];
  publicThemes: ThemeResponse[];
  orgThemes: ThemeResponse[];
  onSave: (values: SavedSettingsUpdateRequest) => Promise<void>;
  isSaving: boolean;
  isLoading: boolean;
  defaultBrandKitId?: string | null;
};

export const PlaybookForm = ({
  playbook,
  contacts,
  brandKits,
  publicThemes,
  orgThemes,
  onSave,
  isSaving,
  isLoading,
  defaultBrandKitId,
}: PlaybookFormProps) => {
  const hasAccessToDebug = useIsAdmin();

  const t = useTranslate();

  // Advanced-settings Collapse is controlled so the save guard can force the
  // Images panel open to reveal the visual-source toggles it's complaining about.
  const [advancedActiveKeys, setAdvancedActiveKeys] = useState<string[]>([]);
  const revealVisualSources = useCallback(() => {
    setAdvancedActiveKeys((keys) =>
      keys.includes(ADVANCED_PANEL_KEYS.images)
        ? keys
        : [...keys, ADVANCED_PANEL_KEYS.images]
    );
  }, []);

  const {
    form,
    hasChanges,
    setHasChanges,
    saveSettings,
    localDuration,
    durationEnabled,
    setDurationOverride,
    setDurationToggleOverride,
  } = usePlaybookForm({
    playbook,
    isAdmin: hasAccessToDebug,
    defaultBrandKitId,
    onSave,
    onVisualSourceMissing: revealVisualSources,
  });

  const selectedAspectRatio = Form.useWatch("aspect_ratio", form);

  // If the user changes aspect ratio, clear the theme when the previously
  // selected theme is incompatible with the newly chosen ratio.
  const resetThemeIfIncompatible = useCallback(
    (nextAspectRatio: SelectableAspectRatio | undefined) => {
      if (!nextAspectRatio) return;
      const currentThemeId = form.getFieldValue("theme_id") as
        | string
        | undefined;
      if (!currentThemeId) return;
      const currentTheme = [...publicThemes, ...orgThemes].find(
        (theme) => theme.id === currentThemeId
      );
      if (currentTheme && currentTheme.aspect_ratio !== nextAspectRatio) {
        form.setFieldValue("theme_id", undefined);
      }
    },
    [form, publicThemes, orgThemes]
  );

  const handleFormChange = (changedValues: Partial<PlaybookFormValues>) => {
    setHasChanges(true);
    if ("aspect_ratio" in changedValues) {
      resetThemeIfIncompatible(changedValues.aspect_ratio);
    }
  };

  const handleDurationChange = (value: number) => {
    setDurationOverride(value);
    setHasChanges(true);
  };

  const handleDurationToggle = (checked: boolean) => {
    setDurationToggleOverride(checked);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <Row gutter={[16, 16]} style={{ padding: "12px 0" }}>
        {["skeleton-1", "skeleton-2", "skeleton-3", "skeleton-4"].map((key) => (
          <Col key={key} xs={24} md={12}>
            <Skeleton variant="rectangular" width="100%" height={32} />
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
      <Flex justify="space-between" align="center">
        <Typography.Title level={2} style={{ margin: 0 }}>
          {playbook.name}
        </Typography.Title>
        <Flex
          justify="end"
          gap={10}
          align="center"
          style={{ marginBottom: 20 }}
        >
          {hasChanges && (
            <Typography.Text type="warning" italic>
              {t("pages.playbooks.unsavedChanges")}
            </Typography.Text>
          )}
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              onClick={() => {
                saveSettings().catch((error) => {
                  console.error("Error saving playbook", error);
                });
              }}
              loading={isSaving}
              disabled={!hasChanges || isSaving}
            >
              {t("pages.playbooks.save")}
            </Button>
          </Form.Item>
        </Flex>
      </Flex>
      <Row gutter={20}>
        <Col xs={24} sm={16}>
          <Card>
            <Row gutter={[16, 0]}>
              <MainSection />
              <BasicSection
                publicThemes={publicThemes}
                orgThemes={orgThemes}
                brandKits={brandKits}
                contacts={contacts}
                selectedAspectRatio={selectedAspectRatio}
                durationEnabled={durationEnabled}
                localDuration={localDuration}
                handleDurationToggle={handleDurationToggle}
                handleDurationChange={handleDurationChange}
              />
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            styles={{
              body: {
                paddingBottom: 0,
              },
            }}
          >
            <AdvancedSection
              isAdmin={hasAccessToDebug}
              activeKeys={advancedActiveKeys}
              onActiveKeysChange={setAdvancedActiveKeys}
            />
          </Card>
        </Col>
      </Row>
    </Form>
  );
};
